import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SectionHeader from '../ui/SectionHeader';
import AnimatedCard from '../ui/AnimatedCard';
import MotionReveal from '../ui/MotionReveal';
import AntButton from '../ui/AntButton';
import { processSteps, skillCategories, personalInfo, sectionCopy } from '../../data/portfolioData';


const Skills = () => {
  const [activeCategory, setActiveCategory] = useState('frontend');
  const currentCategory = skillCategories.find((cat) => cat.id === activeCategory);
  const copy = sectionCopy.skills;

  return (
    <section id="skills" className="section-padding bg-ant-bg text-ant-fg border-t border-ant-line-soft">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-20 mb-16">
          <SectionHeader
            label={copy.label}
            titleLine1={copy.titleLine1}
            titleLine2={copy.titleLine2}
            subtitle={copy.subtitle}
            className="mb-0"
          />

          <MotionReveal delay={100}>
            <AnimatedCard className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-6 hover:border-white/[0.14]">
              <p className="font-mono text-[10px] tracking-widest text-white/25 mb-4">{copy.processLabel}</p>
              <div className="grid grid-cols-2 gap-3">
                {processSteps.map((step) => (
                  <div key={step.step} className="rounded-xl border border-white/[0.06] bg-ant-bg-2 p-4">
                    <span className="font-mono text-[10px] text-ant-gold/70">{step.step}</span>
                    <p className="text-sm font-semibold text-white mt-2">{step.label}</p>
                    <p className="text-[11px] text-white/35 mt-1 leading-relaxed">{step.detail}</p>
                  </div>
                ))}
              </div>
            </AnimatedCard>
          </MotionReveal>
        </div>

        <MotionReveal className="flex flex-wrap gap-2 mb-10">
          {skillCategories.map((category) => (
            <motion.button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              whileTap={{ scale: 0.97 }}
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                activeCategory === category.id
                  ? 'bg-white text-ant-bg-2'
                  : 'border border-white/10 text-white/45 hover:text-white hover:border-white/25'
              }`}
            >
              {category.label}
            </motion.button>
          ))}
        </MotionReveal>

        <div className="grid lg:grid-cols-2 gap-5">
          <MotionReveal>
            <AnimatedCard className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-7 hover:border-white/[0.14]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeCategory}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                  className="space-y-6"
                >
                  {currentCategory?.skills.map((skill) => (
                    <div key={skill.name}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          {skill.icon ? (
                            <img src={skill.icon} alt={skill.name} className="w-6 h-6 object-contain opacity-80" />
                          ) : (
                            <div className="w-6 h-6 rounded bg-white/[0.06] flex items-center justify-center font-mono text-[10px] text-white/40">
                              {skill.name.charAt(0)}
                            </div>
                          )}
                          <span className="text-sm font-medium text-white/80">{skill.name}</span>
                        </div>
                        <span className="font-mono text-[11px] text-white/30">{skill.level}%</span>
                      </div>
                      <div className="h-1 bg-white/[0.06] rounded-full overflow-hidden">
                        <motion.div
                          className="h-full rounded-full bg-gradient-to-r from-ant-gold-dark via-ant-gold to-ant-gold/70"
                          initial={{ width: 0 }}
                          whileInView={{ width: `${skill.level}%` }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                        />
                      </div>
                    </div>
                  ))}
                </motion.div>
              </AnimatePresence>
            </AnimatedCard>
          </MotionReveal>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {skillCategories.flatMap((cat) => cat.skills).map((skill, index) => (
              <MotionReveal key={skill.name} delay={(index % 6) * 60}>
                <AnimatedCard className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5 text-center hover:border-white/[0.12] group h-full">
                  {skill.icon ? (
                    <img
                      src={skill.icon}
                      alt={skill.name}
                      className="h-8 w-8 mx-auto mb-3 opacity-70 group-hover:opacity-100 transition-opacity object-contain"
                    />
                  ) : (
                    <div className="h-8 w-8 mx-auto mb-3 rounded-lg bg-white/[0.04] flex items-center justify-center font-mono text-xs text-ant-gold/70">
                      {skill.name.charAt(0)}
                    </div>
                  )}
                  <p className="text-xs font-medium text-white/50 group-hover:text-white/70 transition-colors">
                    {skill.name}
                  </p>
                </AnimatedCard>
              </MotionReveal>
            ))}
          </div>
        </div>

        <MotionReveal className="mt-16 text-center">
          <AntButton
            variant="secondary"
            href={personalInfo.resumeUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            {copy.resumeCta}
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </AntButton>
        </MotionReveal>
      </div>
    </section>
  );
};

export default Skills;